import React, { useEffect, useState } from 'react';
import { publicApi } from '../../services/api';
import { flattenSettings, getThemeColors, getSettingValue } from '../../utils/settings';
import { parseSectionContent } from '../../utils/sectionContent';

export default function HeroSection({ content }) {
  const { title, subtitle, cta_text, cta_link, cta_secondary_text, cta_secondary_link, badge_text, image_url, logo_url } = content || {};
  const [theme, setTheme] = useState({ primary: '#081626', secondary: '#00CFFF' });
  const [brandLogo, setBrandLogo] = useState('/logo.png');

  useEffect(() => {
    Promise.all([
      publicApi.getSettings().catch(() => ({ data: { data: {} } })),
      publicApi.getPage('home').catch(() => ({ data: { data: { sections: [] } } })),
    ])
      .then(([settingsRes, homeRes]) => {
        const flat = flattenSettings(settingsRes.data?.data || {});
        const colors = getThemeColors(flat);
        const sections = homeRes.data?.data?.sections || [];
        const brandingSection = sections.find(section => section.type === 'branding');
        const brandingContent = parseSectionContent(brandingSection?.content);
        setTheme(colors);
        setBrandLogo(image_url || logo_url || brandingContent.logo_url || brandingContent.image_url || flat.site_logo || '/logo.png');
      })
      .catch(() => {});
  }, []);

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden" style={{ background: `linear-gradient(135deg, ${theme.primary} 0%, ${theme.secondary} 50%, ${theme.secondary}CC 100%)` }}>
      {/* Decorative elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 left-10 w-64 h-64 bg-white rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-accent rounded-full blur-3xl" />
      </div>
      <div className="absolute top-1/4 right-10 animate-float opacity-20 text-8xl">🌿</div>
      <div className="absolute bottom-1/4 left-10 animate-float animation-delay-300 opacity-20 text-6xl">🍃</div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="animate-fadeInUp">
            {badge_text && (
              <span className="inline-block text-white text-sm font-semibold px-4 py-2 rounded-full mb-6" style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}>
                {badge_text}
              </span>
            )}
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white leading-tight mb-4">
              {title || 'Bersihkan dengan Klinnovac'}
            </h1>
            <p className="text-sm sm:text-base mb-6 max-w-lg" style={{ color: 'rgba(255,255,255,0.85)' }}>
              {subtitle || 'Formula eco-enzyme alami untuk kebersihan maksimal tanpa merusak lingkungan'}
            </p>
            <div className="flex flex-wrap gap-4">
              <a href={cta_link || '#products'} className="inline-flex items-center gap-2 bg-white font-semibold px-8 py-4 rounded-full hover:bg-brand-light transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5" style={{ color: theme.primary }}>
                {cta_text || 'Lihat Produk'}
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
              </a>
              <a href={cta_secondary_link || '#about'} className="inline-flex items-center gap-2 border-2 border-white text-white font-semibold px-8 py-4 rounded-full hover:bg-white/10 transition-all">
                {cta_secondary_text || 'Tentang Kami'}
              </a>
            </div>
          </div>
          <div className="hidden lg:flex justify-center animate-fadeInUp animation-delay-200">
            <div className="relative w-96 h-96 bg-white/10 rounded-full flex items-center justify-center">
              <div className="w-80 h-80 bg-white/20 rounded-full flex items-center justify-center">
                <div className="w-64 h-64 bg-white/30 rounded-full flex items-center justify-center p-4" style={{ boxShadow: `0 0 0 20px ${theme.secondary}22` }}>
                  <img src={brandLogo} alt="Hero logo" className="w-full h-full object-contain" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <svg className="w-6 h-6 text-white/60" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" /></svg>
      </div>
    </section>
  );
}